import { useState, useCallback } from 'react';
import { DRAG_THRESHOLD } from '../constants';
import { calculateDistance } from '../utils/canvas';

/**
 * Custom hook for canvas interaction (drag, pan, selection)
 * @param {Object} scrollContainerRef - Ref to scroll container
 * @returns {Object} Interaction state and handlers
 */
export const useCanvasInteraction = (scrollContainerRef, zoom = 1) => {
  // Drag state
  const [dragState, setDragState] = useState({
    isDragging: false,
    nodeId: null,
    startX: 0,
    startY: 0,
    distance: 0,
    originalPositions: {}
  });

  // Pan state
  const [panState, setPanState] = useState({
    isPanning: false,
    startX: 0,
    startY: 0,
    scrollLeft: 0,
    scrollTop: 0
  });

  // Selection state
  const [selectionState, setSelectionState] = useState({
    isSelecting: false,
    startX: 0,
    startY: 0,
    endX: 0,
    endY: 0
  });


  /**
   * Handle mouse down on a node
   * @param {MouseEvent} e - Mouse event
   * @param {string} nodeId - Node ID
   * @param {Array} nodes - Array of nodes
   * @param {Array} selectedNodes - Array of selected node IDs
   * @param {Function} setSelectedNodes - Function to set selected nodes
   */
  const handleMouseDown = useCallback((e, nodeId, nodes, selectedNodes, setSelectedNodes) => {
    const node = nodes.find(n => n.id === nodeId);
    if (!node) return;

    e.stopPropagation();

    // Ctrl/Cmd for multi-selection
    if (e.ctrlKey || e.metaKey) {
      if (selectedNodes.includes(nodeId)) {
        setSelectedNodes(prev => prev.filter(id => id !== nodeId));
      } else {
        setSelectedNodes(prev => [...prev, nodeId]);
      }
      return;
    }

    // Single click does NOT touch selectedNodes.
    // If the dragged node is part of an existing multi-selection, drag all of them;
    // otherwise drag only this node.
    const dragNodes = selectedNodes.includes(nodeId) ? selectedNodes : [nodeId];

    const originalPositions = {};
    dragNodes.forEach(id => {
      const n = nodes.find(node => node.id === id);
      if (n) {
        originalPositions[id] = { x: n.x, y: n.y };
      }
    });

    setDragState({
      isDragging: true,
      nodeId,
      startX: e.clientX - node.x * zoom,
      startY: e.clientY - node.y * zoom,
      distance: 0,
      originalPositions
    });
  }, [zoom]);

  /**
   * Handle mouse move
   * @param {MouseEvent} e - Mouse event
   * @param {Array} nodes - Array of nodes
   * @param {Function} setNodes - Function to set nodes
   * @param {Array} selectedNodes - Array of selected node IDs
   */
  const handleMouseMove = useCallback((e, nodes, setNodes) => {
    // Panning
    if (panState.isPanning && scrollContainerRef.current) {
      const dx = e.clientX - panState.startX;
      const dy = e.clientY - panState.startY;
      scrollContainerRef.current.scrollLeft = panState.scrollLeft - dx;
      scrollContainerRef.current.scrollTop = panState.scrollTop - dy;
      return;
    }

    // Selection rectangle
    if (selectionState.isSelecting && scrollContainerRef.current) {
      const rect = scrollContainerRef.current.getBoundingClientRect();
      const canvasX = (e.clientX - rect.left + scrollContainerRef.current.scrollLeft) / zoom;
      const canvasY = (e.clientY - rect.top + scrollContainerRef.current.scrollTop) / zoom;

      setSelectionState(prev => ({
        ...prev,
        endX: canvasX,
        endY: canvasY
      }));
      return;
    }

    // Dragging nodes
    if (!dragState.isDragging) return;

    const newX = (e.clientX - dragState.startX) / zoom;
    const newY = (e.clientY - dragState.startY) / zoom;
    const draggedNode = nodes.find(n => n.id === dragState.nodeId);
    
    if (!draggedNode) return;
    
    const distance = calculateDistance(newX, newY, draggedNode.x, draggedNode.y);

    // Move all nodes tracked in originalPositions (may be 1 or many)
    const trackedIds = Object.keys(dragState.originalPositions);
    if (trackedIds.length > 1) {
      const dx = newX - dragState.originalPositions[dragState.nodeId].x;
      const dy = newY - dragState.originalPositions[dragState.nodeId].y;

      setNodes(prev => prev.map(node => {
        if (dragState.originalPositions[node.id]) {
          return {
            ...node,
            x: dragState.originalPositions[node.id].x + dx,
            y: dragState.originalPositions[node.id].y + dy
          };
        }
        return node;
      }));
    } else {
      setNodes(prev => prev.map(node =>
        node.id === dragState.nodeId
          ? { ...node, x: newX, y: newY }
          : node
      ));
    }

    setDragState(prev => ({ ...prev, distance }));
  }, [dragState, panState, selectionState, scrollContainerRef, zoom]);

  /**
   * Handle mouse up
   * @param {Array} nodes - Array of nodes
   * @param {Function} setSelectedNodes - Function to set selected nodes
   * @returns {Object|null} Clicked node info if it was a click (not drag)
   */
  const handleMouseUp = useCallback((nodes, setSelectedNodes) => {
    // Reset panning
    if (panState.isPanning) {
      setPanState({ isPanning: false, startX: 0, startY: 0, scrollLeft: 0, scrollTop: 0 });
      return null;
    }

    // Finalize selection
    if (selectionState.isSelecting) {
      const { startX, startY, endX, endY } = selectionState;
      
      const selectionWidth = Math.abs(endX - startX);
      const selectionHeight = Math.abs(endY - startY);
      
      // Clear selection if rectangle is too small
      if (selectionWidth < 5 && selectionHeight < 5) {
        setSelectedNodes([]);
        setSelectionState({ isSelecting: false, startX: 0, startY: 0, endX: 0, endY: 0 });
        return null;
      }
      
      // Find nodes in selection rectangle
      const minX = Math.min(startX, endX);
      const maxX = Math.max(startX, endX);
      const minY = Math.min(startY, endY);
      const maxY = Math.max(startY, endY);
      
      const nodesInSelection = nodes.filter(node => {
        return node.x >= minX && node.x <= maxX && node.y >= minY && node.y <= maxY;
      });
      
      setSelectedNodes(nodesInSelection.map(n => n.id));
      setSelectionState({ isSelecting: false, startX: 0, startY: 0, endX: 0, endY: 0 });
      return null;
    }

    // Reset drag state
    if (dragState.isDragging) {
      const wasClick = dragState.distance <= DRAG_THRESHOLD;
      setDragState({ isDragging: false, nodeId: null, startX: 0, startY: 0, distance: 0, originalPositions: {} });
      return wasClick ? dragState.nodeId : null;
    }

    return null;
  }, [dragState, panState, selectionState]);

  /**
   * Handle mouse down on canvas
   * @param {MouseEvent} e - Mouse event
   * @param {Function} setSelectedNode - Function to set selected node
   * @param {Function} setModalNode - Function to set modal node
   * @param {Function} setSelectedNodes - Function to set selected nodes
   */
  const handleCanvasMouseDown = useCallback((e, setSelectedNode, setModalNode, setSelectedNodes, currentIsPanMode) => {
    // Pan mode (toggle button or spacebar)
    if (currentIsPanMode) {
      e.preventDefault();
      setSelectedNode(null);
      setModalNode(null);
      setSelectedNodes([]);
      if (scrollContainerRef.current) {
        setPanState({
          isPanning: true,
          startX: e.clientX,
          startY: e.clientY,
          scrollLeft: scrollContainerRef.current.scrollLeft,
          scrollTop: scrollContainerRef.current.scrollTop
        });
      }
      return;
    }

    if (e.target.tagName.toLowerCase() !== 'svg' && e.target.tagName.toLowerCase() !== 'rect') {
      return;
    }

    // Always deselect nodes when clicking on canvas
    setSelectedNode(null);
    setModalNode(null);
    setSelectedNodes([]);

    // Start selection rectangle (only in selection mode)
    if (scrollContainerRef.current) {
      const rect = scrollContainerRef.current.getBoundingClientRect();
      const canvasX = (e.clientX - rect.left + scrollContainerRef.current.scrollLeft) / zoom;
      const canvasY = (e.clientY - rect.top + scrollContainerRef.current.scrollTop) / zoom;

      setSelectionState({
        isSelecting: true,
        startX: canvasX,
        startY: canvasY,
        endX: canvasX,
        endY: canvasY
      });
    }
  }, [scrollContainerRef, zoom]);

  return {
    dragState,
    panState,
    selectionState,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleCanvasMouseDown
  };
};
